import React from 'react';
import styled from 'styled-components';
import { Bleed, Box, Card, Heading, Inline, Link, SubtleText, Tag } from '@99designs/emu';
import { boxShadows, colors } from '@99designs/emu/settings';
import { FormattedReactMessage, formatMessage } from '@99designs/frontend-components/i18n';
import { CurrencyCode } from '@99designs/i18n';
import { Price } from '../Price';
import { FeaturesList } from '../contest-packages/FeaturesList';

const StyledCard = styled(Card)`
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
    position: relative;

    &:hover {
        box-shadow: ${boxShadows.high};
        cursor: pointer;
    }

    &:focus-visible {
        outline: none;
        box-shadow: ${boxShadows.focusRing}, ${boxShadows.high};
    }
`;

const StyledLink = styled(Link)`
    all: unset;

    &:hover,
    &:focus {
        all: unset;
    }

    /* Styles to make entire card clickable */
    &::after {
        content: '';
        display: block;
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
    }
`;

const DesignerImage = styled.img`
    min-height: 100%;
    object-fit: cover;
`;

const TruncatedHeading = styled(Heading)`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
`;

const getAltText = (image: Image) => {
    if (image.alt) {
        return image.alt;
    } else if (image.designerAttribution?.text) {
        return formatMessage(__('Design by {displayName}'), {
            displayName: image.designerAttribution?.text,
        });
    } else {
        return '';
    }
};

export interface ProductCardProps {
    url: string;
    title: string;
    description: string;
    price?: Price;
    saving?: string;
    image: Image;
    deliverables?: string[];
}

interface Price {
    currency: CurrencyCode;
    amountInCents: number;
}

interface Image {
    src: string;
    alt?: string;
    designerAttribution?: {
        text: string;
        userId?: string;
    } | null;
}

export const ProductCard = React.forwardRef<
    HTMLButtonElement | HTMLAnchorElement,
    ProductCardProps
>(({ url, title, description, price, saving, image, deliverables }, ref) => {
    return (
        <Box width={['260px', '330px', '392px', '392px']} height="100%">
            <StyledCard appearance="elevated">
                <Bleed spaceTop="1.5bu" spaceX="1.5bu">
                    <Box
                        width="100%"
                        height={['173px', '226px', '261px', '261px']}
                        background={colors.grey200}
                        marginBottom="1.5bu"
                    >
                        <DesignerImage
                            src={image.src}
                            className="lazyload"
                            alt={getAltText(image)}
                        />
                    </Box>
                </Bleed>
                <Box display="flex" flexDirection="column" height="100%">
                    <Box flexGrow={1}>
                        <TruncatedHeading appearanceLevel={5} accessibilityLevel={7} as="p">
                            <StyledLink href={url} ref={ref}>
                                {title}
                            </StyledLink>
                        </TruncatedHeading>
                        <Box paddingTop="0.5bu">
                            <SubtleText as="p" truncate={[5, 5, 4, 4]}>
                                {description}
                            </SubtleText>
                        </Box>
                        {deliverables && (
                            <Box paddingTop="0.5bu">
                                <FeaturesList features={deliverables} />
                            </Box>
                        )}
                    </Box>
                    {price && (
                        <Box paddingTop="1bu">
                            <Inline space="0.5bu">
                                <Heading appearanceLevel={4} accessibilityLevel={7} as="p">
                                    <FormattedReactMessage
                                        message={__('from <Price>price</Price>')}
                                        replacements={{
                                            Price: (
                                                <Price
                                                    amount={price.amountInCents}
                                                    currency={price.currency}
                                                    minimumFractionDigits={0}
                                                />
                                            ),
                                        }}
                                    />
                                </Heading>
                                {saving && <Tag appearance="filled">{saving}</Tag>}
                            </Inline>
                        </Box>
                    )}
                </Box>
            </StyledCard>
        </Box>
    );
});
